import { NullableDateValue } from '@/util/DateHelper';
import { IRideitNotice, NoticePriority, NoticeStatus, RideitNotice } from "./RideitNotice";
import { NoticeTargetType, RideitNoticeTarget } from "./RideitNoticeTarget";

export const SELECT_ALL_VALUE = -1 as const;

export interface IRideitNoticeFormData {
  title: string;
  text: string;
  externalLink: string;
  validFrom?: moment.Moment | Date | string | null;
  validUntil?: moment.Moment | Date | string | null;
  priority: NoticePriority;
  targets: RideitNoticeTarget[];

  destinationIds: number[];
  passengerGroupIds: number[];
  passengerIds: number[];

  organizationId: string;
}

export class RideitNoticeFormData implements IRideitNoticeFormData {
  title: string;
  text: string;
  externalLink: string;
  validFrom: NullableDateValue;
  validUntil: NullableDateValue;
  priority: NoticePriority;
  targets: RideitNoticeTarget[];
  status: NoticeStatus;

  destinationIds: number[];
  passengerGroupIds: number[];
  passengerIds: number[];

  organizationId: string | null;

  originalNotice: RideitNotice | null;

  constructor(data?: IRideitNotice | null, organizationId?: string | null) {
    this.title = data?.title ?? '';
    this.text = data?.text ?? '';
    this.externalLink = data?.externalLink ?? '';
    this.validFrom = data?.validFrom ?? null;
    this.validUntil = data?.validUntil ?? null;
    this.priority = data?.priority ?? NoticePriority.Info;
    this.targets = data?.targets ?? [];

    this.destinationIds = data?.targets?.filter((t) => t.targetType === NoticeTargetType.Destination)
        .map((t) => t.destinationId ?? SELECT_ALL_VALUE) ?? [];

    this.passengerGroupIds = data?.targets?.filter((t) => t.targetType === NoticeTargetType.PassengerGroup)
        .map((t) => t.passengerGroupId ?? SELECT_ALL_VALUE) ?? [];

    this.passengerIds = data?.targets?.filter((t) => t.targetType === NoticeTargetType.Passenger)
        .map((t) => t.passengerId ?? SELECT_ALL_VALUE) ?? [];

    this.organizationId = organizationId ?? null;
    this.status = data?.status ?? NoticeStatus.Active;

    this.originalNotice = data ? new RideitNotice(data) : null;
  }

  toNotice() {
    const notice = new RideitNotice();
    notice.title = this.title;
    notice.text = this.text;
    notice.externalLink = this.externalLink;
    notice.validFrom = this.validFrom;
    notice.validUntil = this.validUntil;
    notice.priority = this.priority;
    notice.targets = this.getNoticeTargets();
    notice.status = this.status;

    if (this.originalNotice?.id) {
      notice.id = this.originalNotice.id;
    }

    if (this.originalNotice?.createdAt) {
      notice.createdAt = this.originalNotice.createdAt;
    }
    if (this.originalNotice?.createdBy) {
      notice.createdBy = this.originalNotice.createdBy;
    }

    return notice;
  }

  private getNoticeTargets() {
    let targets: RideitNoticeTarget[] = [];
    targets = targets.concat(this.getDestinationTargets());
    targets = targets.concat(this.getPassengerGroupTargets());
    targets = targets.concat(this.getPassengerTargets());
    return targets;
  }

  private getDestinationTargets() {
    if (this.isAllSelected(this.destinationIds)) {
      return [ this.getNoticeTarget(-1, NoticeTargetType.Destination, this.organizationId ) ];
    }
    return this.destinationIds.map((id) => this.getNoticeTarget(id, NoticeTargetType.Destination, this.organizationId));
  }

  private getPassengerGroupTargets() {
    if (this.isAllSelected(this.passengerGroupIds)) {
      return [ this.getNoticeTarget(-1, NoticeTargetType.PassengerGroup, this.organizationId ) ];
    }
    return this.passengerGroupIds.map((id) => this.getNoticeTarget(id, NoticeTargetType.PassengerGroup, this.organizationId));
  }

  private getPassengerTargets() {
    if (this.isAllSelected(this.passengerIds)) {
      return [ this.getNoticeTarget(-1, NoticeTargetType.Passenger, this.organizationId ) ];
    }
    return this.passengerIds.map((id) => this.getNoticeTarget(id, NoticeTargetType.Passenger, this.organizationId));
  }

  private isAllSelected(ids: number[]) {
    return ids.length === 1 && ids[0] === SELECT_ALL_VALUE;
  }

  private getNoticeTarget(id: number, type: NoticeTargetType, organizationId: string | null) {
    const existingTarget = this.getExistingNoticeTarget(id, type, organizationId);
    if (existingTarget) {
      return existingTarget;
    }

    const target = new RideitNoticeTarget();
    target.targetType = type;
    target.organizationId = organizationId;

    if (this.originalNotice?.id) {
      target.noticeId = this.originalNotice.id;
    }

    if (id === SELECT_ALL_VALUE) {
      return target;
    }

    switch (type) {
      case NoticeTargetType.Destination:
        target.destinationId = id;
        break;
      case NoticeTargetType.PassengerGroup:
        target.passengerGroupId = id;
        break;
      case NoticeTargetType.Passenger:
        target.passengerId = id;
        break;
    }

    return target;
  }

  private getExistingNoticeTarget(id: number, type: NoticeTargetType, organizationId: string | null) {
    let target = this.originalNotice?.targets.find(t => {
      if (t.targetType !== type || t.organizationId !== organizationId) {
        return false;
      }

      switch (type) {
        case NoticeTargetType.Destination:
          return t.destinationId === id || (id === SELECT_ALL_VALUE && t.destinationId === null);
        case NoticeTargetType.PassengerGroup:
          return t.passengerGroupId === id || (id === SELECT_ALL_VALUE && t.passengerGroupId === null);
        case NoticeTargetType.Passenger:
          return t.passengerId === id || (id === SELECT_ALL_VALUE && t.passengerId === null);
      }
    });

    return target ?? null;
  }

}
